import * as Sentry from "@sentry/react-native";
import dayjs from "dayjs";
import * as AuthSession from "expo-auth-session";
import { router } from "expo-router";
import { jwtDecode } from "jwt-decode";
import { autorun } from "mobx";
import {
  cast,
  flow,
  types,
  type Instance,
  type SnapshotIn,
  type SnapshotOut,
} from "mobx-state-tree";
import { z } from "zod";
import { constants } from "~/constants";
import { environment } from "~/environment";
import { assert } from "~/utils/assert";
import { delay } from "~/utils/delay";
import { User } from "./entities/user/User";
import { getEnv } from "./utils/getEnv";
import { getRoot } from "./utils/getRoot";
import type { UserInstance } from "./entities/user/User";
import type { Await } from "./utils/Await";
import type { Href } from "expo-router";

export interface AuthStoreInstance extends Instance<typeof AuthStore> {}
export interface AuthStoreSnapshotIn extends SnapshotIn<typeof AuthStore> {}
export interface AuthStoreSnapshotOut extends SnapshotOut<typeof AuthStore> {}

export const AuthStore = types
  .model("AuthStore", {
    activeUser: types.safeReference(User),
    activeUserNameFromAuthProvider: types.optional(types.string, ""),
    accessToken: types.maybe(types.string),
    refreshToken: types.maybe(types.string),
  })
  .views((self) => {
    return {
      get isLoggedIn() {
        return !!self.accessToken && !!self.activeUser;
      },
    };
  })
  .volatile(() => ({
    refreshTokenPromise: null as Promise<string | undefined> | null,
  }))
  .actions((self) => {
    return {
      setActiveUser(idOrInstance: string | UserInstance) {
        self.activeUser = cast(idOrInstance);
        return self.activeUser;
      },
      clearActiveUser() {
        self.activeUser = undefined;
      },
    };
  })
  .actions((self) => {
    return {
      getActiveUser: flow<UserInstance, []>(function* getActiveUser(): any {
        const env = getEnv(self);
        const root = getRoot(self);

        const response = yield env.baseAuthHttp.get("clients/me");

        const user = root.userStore.processOne(response.data.data);

        self.setActiveUser(user);

        return user;
      }),

      createNewUser: flow<
        UserInstance,
        [
          {
            firstName: string;
            lastName: string;
            streetAddress: string;
            zipCode: string;
            city: string;
            email: string;
            phoneNumber: string;
          },
        ]
      >(function* createNewUser(userDataArg): any {
        const env = getEnv(self);
        const root = getRoot(self);

        const userDataSchema = z.object({
          firstName: z.string(),
          lastName: z.string(),
          streetAddress: z.string(),
          zipCode: z.string(),
          city: z.string(),
          email: z.string(),
          phoneNumber: z.string(),
        });

        const userData = userDataSchema.parse({
          firstName: userDataArg.firstName,
          lastName: userDataArg.lastName,
          streetAddress: userDataArg.streetAddress,
          zipCode: userDataArg.zipCode,
          city: userDataArg.city,
          email: userDataArg.email,
          phoneNumber: userDataArg.phoneNumber,
        });

        const response = yield env.baseAuthHttp.post("clients/me", userData);

        const user = root.userStore.processOne(response.data.data);

        self.setActiveUser(user);

        return user;
      }),
    };
  })
  .actions((self) => {
    return {
      logout(redirectHref: Href) {
        self.accessToken = undefined;
        self.activeUser = undefined;
        router.dismissAll();
        router.replace(redirectHref);
      },
    };
  })
  .actions((self) => {
    const _performTokenRefresh = flow<string | undefined, []>(
      function* _performTokenRefresh() {
        try {
          console.log("🔒 Refresh Lock Acquired: Performing token refresh...");

          const discovery: Await<typeof AuthSession.resolveDiscoveryAsync> =
            yield AuthSession.resolveDiscoveryAsync(
              environment.OIDC_DISCOVERY_URL,
            );

          const tokenResponse: Await<typeof AuthSession.refreshAsync> =
            yield AuthSession.refreshAsync(
              {
                clientId: environment.OIDC_CLIENT_ID,
                refreshToken: self.refreshToken,
                scopes: environment.OIDC_SCOPES as string[],
              },
              discovery,
            );

          const accessToken = tokenResponse.accessToken;
          const newRefreshToken = tokenResponse.refreshToken;

          assert(newRefreshToken, "New refreshToken is required from provider");

          // Update the store with the new tokens
          self.accessToken = accessToken;
          self.refreshToken = newRefreshToken;

          console.log("✅ Token refresh successful.");
          return self.accessToken;
        } catch (error) {
          // Clear tokens, this will log the user out
          self.accessToken = undefined;
          self.refreshToken = undefined;
          throw error; // Re-throw the error so waiting requests also fail
        }
      },
    );

    return {
      setAccessToken: (accessToken?: string) => {
        self.accessToken = accessToken;
      },

      login: flow<boolean, any>(function* login({
        discovery,
        authRequest,
      }: {
        discovery: AuthSession.DiscoveryDocument;
        authRequest: AuthSession.AuthRequest;
      }): any {
        const codeVerifier = authRequest.codeVerifier;

        assert(codeVerifier, "codeVerifier is required");

        const authSessionResult: Await<typeof authRequest.promptAsync> =
          yield authRequest.promptAsync(discovery, {
            windowFeatures: {
              popup: false,
            },
          });

        if (authSessionResult.type === "dismiss") {
          throw new Error("Login was dismissed");
        }

        if (authSessionResult.type === "cancel") {
          throw new Error("Login was cancelled");
        }

        if (authSessionResult.type === "error") {
          console.error("AuthSession error:", authSessionResult.error);

          authSessionResult.error?.code &&
            Sentry.captureException(
              new Error(
                `Login failed with code: ${authSessionResult.error.code}, more info: ${authSessionResult.error.uri}`,
              ),
            );
        }

        if (authSessionResult.type !== "success") {
          throw new Error(
            "An error occurred during login. Please try again later.",
          );
        }

        const authorizationCode = authSessionResult.params.code;

        assert(authorizationCode, "authorizationCode is required");

        const tokenResponse: Await<typeof AuthSession.exchangeCodeAsync> =
          yield AuthSession.exchangeCodeAsync(
            {
              clientId: environment.OIDC_CLIENT_ID,
              code: authorizationCode,
              redirectUri: authRequest.redirectUri,
              scopes: environment.OIDC_SCOPES as string[],
              extraParams: {
                code_verifier: codeVerifier,
              },
            },
            discovery,
          );

        const accessToken = tokenResponse.accessToken;
        const refreshToken = tokenResponse.refreshToken;

        assert(refreshToken, "refreshToken is required");

        self.accessToken = accessToken;
        self.refreshToken = refreshToken;

        // // When we set accessToken it triggers the autorun to set the header to the baseAuthHttp axios instance
        // // However, this is not instant, so we need to await a delay of 0 ms just so that all the functions in the stack
        // // are executed before the request including the one that sets the header
        yield delay(0);

        return true;
      }),

      silentLogin: flow(function* silentLogin(): any {
        try {
          const env = getEnv(self);
          const root = getRoot(self);

          // If accessToken we know the user isn't logged in.
          if (!self.accessToken) return false;

          const response = yield env.baseAuthHttp.get("clients/me");

          const user = root.userStore.processOne(response.data.data);

          self.setActiveUser(user);

          return true;
        } catch (error: any) {
          console.log("error in silent login", error.message);
          // Sentry.captureException(error);
          self.accessToken = undefined;
          self.refreshToken = undefined;
          return false;
        }
      }),

      updateTokens: flow<string | undefined, []>(function* updateTokens() {
        if (!self.refreshToken || !self.accessToken) {
          // Can't refresh if we don't have the necessary tokens
          return undefined;
        }

        const tokenDecoded = jwtDecode(self.accessToken);
        const expirationTimeUnix = tokenDecoded.exp;

        if (!expirationTimeUnix) {
          throw new Error("🔑 Error: Token does not have expiration time");
        }

        const timeLeft = dayjs.unix(expirationTimeUnix).diff(dayjs(), "second");
        const isExpired = timeLeft < 30;

        if (!isExpired) {
          // Token is still valid, no action needed
          return self.accessToken;
        }

        // 👇 2. Check if a refresh is already in progress.
        if (self.refreshTokenPromise) {
          console.log(
            "🔑 Waiting for an in-progress token refresh to complete...",
          );
          // If yes, wait for the existing promise to resolve
          return yield self.refreshTokenPromise;
        }

        // 👇 3. If no refresh is in progress, start one and store the promise.
        console.log("🔑 Token expired. Initiating a new token refresh.");
        self.refreshTokenPromise = _performTokenRefresh();

        try {
          // 👇 4. Await the result of the refresh operation.
          return yield self.refreshTokenPromise;
        } finally {
          // 👇 5. CRITICAL: Release the lock after the operation is complete.
          // This ensures that subsequent calls can trigger a new refresh if needed.
          console.log("🔓 Refresh Lock Released.");
          self.refreshTokenPromise = null;
        }
      }),

      initializeAccessTokenSync: flow(
        function* initializeAccessTokenSync(): any {
          const env = getEnv(self);

          // sync refreshToken to persistence
          const refreshToken = yield env.persistence.get(
            constants.ASYNC_STORAGE_KEYS.REFRESH_TOKEN,
          );
          self.refreshToken = refreshToken;

          autorun(() => {
            env.persistence.set(
              constants.ASYNC_STORAGE_KEYS.REFRESH_TOKEN,
              self.refreshToken,
            );
          });
          // END - sync refreshToken to persistence

          // sync access token to api and persistence
          const accessTokenFromStorage = yield env.persistence.get(
            constants.ASYNC_STORAGE_KEYS.ACCESS_TOKEN,
          );
          self.accessToken = accessTokenFromStorage;

          autorun(() => {
            env.persistence.set(
              constants.ASYNC_STORAGE_KEYS.ACCESS_TOKEN,
              self.accessToken,
            );
          });

          // We no longer need this since Authorization header is set in the baseAuthHttp interceptor

          // autorun(() => {
          //   if (self.accessToken) {
          //     env.baseAuthHttp.defaults.headers.common["Authorization"] =
          //       `Bearer ${self.accessToken}`;
          //   } else {
          //     delete env.baseAuthHttp.defaults.headers.common["Authorization"];
          //   }
          // });
        },
      ),
    };
  });
